'use client';

import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { todaysOfficeHour } from './todaysOfficeHour';
import type { LocationSearchResultsResolved } from './types';
import { Icon, IconType } from '@/components/Icon';
import { Link } from '@/components/Link';
import type { LocationSearchResultDto } from '@/lib/api';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';

// CMS placeholder for the hits of the current `?locationSearchTerm=`
// (10.02.11). The route resolves them server-side and hands them over through
// the render context, so the HTML the edge caches already carries the list —
// this widget never fetches. Outside `/standorte/suche/` there is no
// `locationSearch` entity and the widget renders nothing.
export function LocationSearchResultsComponent({
  resolved,
}: {
  resolved: LocationSearchResultsResolved;
}) {
  const t = useTranslations('locationSearchResults');
  const { entities } = useRenderContext();
  const search = entities.locationSearch;

  // No search context at all (any page but `/standorte/suche/`), or a visitor
  // who has not searched yet: render nothing. The empty state is the answer to
  // "we looked and found none", which would be wrong copy for someone who
  // never typed a term — and the heading echoes the term back inside literal
  // angle brackets, so it would read `Ihre Suche nach <> ergab folgende
  // Treffer:`. Both states are reachable: a bare `/standorte/suche/`, and
  // submitting the Location Finder with an empty input.
  if (!search?.term) {
    return null;
  }

  const { term, results } = search;
  // An editor-supplied title carries the raw `{searchTerm}` token; the
  // default lives in ICU, where next-intl substitutes it.
  const title = resolved.resultsTitle
    ? resolved.resultsTitle.replaceAll('{searchTerm}', term)
    : t('resultsTitle', { searchTerm: term });

  return (
    <section className="my-6 md:my-12">
      {/* The section headline stands whether or not anything matched — it
          names the section. The results title below it introduces the hits,
          so it only appears when there are some. */}
      <p className="mb-4 text-center text-base tracking-widest">
        {resolved.sectionTitle || t('sectionTitle')}
      </p>
      {results.length > 0 && <h2 className="mb-6 text-center">{title}</h2>}

      {results.length === 0 ? (
        resolved.emptyText && resolved.emptyText.length > 0 ? (
          <div className="my-12 text-center">
            <RichText nodes={resolved.emptyText} />
          </div>
        ) : (
          <div className="my-12 text-center">
            <h3>{t('emptyText1')}</h3>
            <p className="mt-2">{t('emptyText2')}</p>
          </div>
        )
      ) : (
        // A single-column list at every breakpoint, not the City Page's
        // two-column grid: the hits are a search result to scan top to bottom,
        // and their count varies from one to a hundred.
        <ul className="flex list-none flex-col gap-3 px-0 md:gap-5">
          {results.map((location) => (
            <LocationSearchResultCard
              key={location.slug}
              location={location}
              linkLabel={resolved.locationLinkLabel || t('locationLinkLabel')}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function LocationSearchResultCard({
  location,
  linkLabel,
}: {
  location: LocationSearchResultDto;
  linkLabel: string;
}) {
  const t = useTranslations('locationSearchResults');
  const address = [
    location.street,
    [location.postcode, location.city].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(' | ');

  const today = todaysOfficeHour(location.officeHours);
  // No entry for today → no line at all, rather than an empty label.
  // `<label>` sets the "Öffnungszeiten:" part apart from the hours. It lives
  // inside the message rather than being concatenated here, so a translation
  // can punctuate and place it its own way — French needs the space before
  // its colon, and the label is not always the first word.
  const labelTag = (chunks: ReactNode) => (
    <span className="font-medium">{chunks}</span>
  );
  const openingHours = !today
    ? undefined
    : today.closed
      ? t.rich('openingHoursTodayClosed', { label: labelTag })
      : t.rich('openingHoursToday', {
          label: labelTag,
          // Non-null by construction: `todaysOfficeHour()` drops an open entry
          // that carries no times.
          start: today.startTime!,
          end: today.endTime!,
        });

  return (
    <li className="bg-secondary-lighter relative my-0 flex flex-col items-center rounded-2xl px-4 py-6 text-center md:items-start md:px-10 md:py-8">
      <div className="flex flex-col items-center gap-1 md:items-start">
        <h3 className="mb-2.5 text-base font-medium md:text-2xl">
          {t('cardTitle', { name: location.name })}
        </h3>
        {address && <p className="my-0">{address}</p>}
        {openingHours && <p className="my-0">{openingHours}</p>}
      </div>
      {/* The "➔ Label" arrow link, in the brand blue `LinkList` uses for the
          same shape — the icon is a `bg-current` mask, so colouring the link
          colours the glyph with it. The ::after overlay stretches the link
          over the whole tile, so the card navigates as one. */}
      <Link
        href={`/nachhilfe/${location.slug}/`}
        className="text-primary my-3 inline-flex items-center gap-2 font-medium after:absolute after:inset-0"
      >
        <Icon src={IconType.SvgArrowRightBold} size={16} className="shrink-0" />
        {linkLabel}
      </Link>
    </li>
  );
}
